import { Box, CircularProgress, Typography } from '@mui/material'
import { Fragment, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Context from '../Context'
import theme from '../theme'
import { FONT, cellSx, headSx, MOVE_TINT, MOVE_MS } from './board'
import { MAX_EVENTS } from './BoardFilters'
import { useClock } from '../utils/clock'
import { pathForEvent } from '../utils/routes'
import { findSoftRow, findSharpRow } from '../utils/arb'
import { formatDecimalPrice } from '../utils/price'
import Favorite from './Favorite'
import EventMarkets from './EventMarkets'

// Eight thousand fixtures of unfiltered feed is a page the browser fights with rather than a board
// anybody reads. The cap is counted in games rather than rows, because a game is what somebody is
// looking for and its six or seven selections belong together; the line above the table says what was
// left out, since a board that quietly stops is a board where next week's games look like they are gone.
const MAX_GAMES = 200

/**
 * One row per selection rather than per game. The comparison feed already carries the main line for each
 * market — one handicap, one total — so that is what appears here; the ladders live on the event screen.
 * The yield published on a sharp row is measured against that book, which makes Pinnacle's copy the gap
 * against Pinnacle without recomputing anything.
 */
const ROWS = [
  { market: '1X2', selection: (e) => e.home, price: 'homePrice', yield: 'homeYield' },
  { market: '1X2', selection: () => 'Draw', price: 'drawPrice', yield: 'drawYield' },
  { market: '1X2', selection: (e) => e.away, price: 'awayPrice', yield: 'awayYield' },
  {
    market: 'AH',
    selection: (e, row) => `${e.home} ${row.spreadPoints}`,
    price: 'homeSpreadPrice',
    yield: 'homeSpreadYield',
    needs: 'spreadPoints'
  },
  {
    market: 'AH',
    selection: (e, row) => `${e.away} ${-row.spreadPoints}`,
    price: 'awaySpreadPrice',
    yield: 'awaySpreadYield',
    needs: 'spreadPoints'
  },
  {
    market: 'AOU',
    selection: (e, row) => `Over ${row.totalPoints}`,
    price: 'overPrice',
    yield: 'overYield',
    needs: 'totalPoints'
  },
  {
    market: 'AOU',
    selection: (e, row) => `Under ${row.totalPoints}`,
    price: 'underPrice',
    yield: 'underYield',
    needs: 'totalPoints'
  }
]

const BETFAIR = /betfair/i

const usable = (price) => typeof price === 'number' && price > 1

/**
 * A row per selection: our price, Pinnacle's, Betfair's, and the distance between the first two. The
 * board around it owns the feed and the filters — this draws them.
 */
function SelectionsTable({ events, error, filters, pollingInterval, feedToken }) {
  const { usPrices } = useContext(Context)
  const navigate = useNavigate()
  const clock = useClock()

  /**
   * Which games have been opened up, by home|away|kick off rather than by position, so a game keeps its
   * panel when the board is rebuilt around it. Every line of the match is drawn by the same component the
   * event screen uses, and its prices open the same history window — the point of the panel is that
   * reading a match no longer costs leaving the board and finding your place again.
   */
  const [ open, setOpen ] = useState(() => new Set())
  const toggle = (key) => setOpen((prev) => {
    const next = new Set(prev)
    if (next.has(key)) {
      next.delete(key)
    } else {
      next.add(key)
    }
    return next
  })

  const { wantsMarket, inGap } = filters

  /**
   * The board is settled when it is drawn and again whenever a filter moves — never in between. A list
   * that quietly grows a row every couple of seconds, or drops one because a price crossed the edge of
   * the band while it was being read, is a list nobody can work down. What does keep moving is the
   * prices: each poll writes the new numbers into the rows that are already there.
   */
  const eventsRef = useRef(events)
  eventsRef.current = events

  const buildRows = () => {
    const out = []

    for (const event of eventsRef.current || []) {

      const soft = findSoftRow(event.bookieList)
      const sharp = findSharpRow(event.bookieList)
      const betfair = (event.bookieList || []).find((row) => BETFAIR.test(row.bookmaker || ''))
      if (!soft || !sharp) {
        continue
      }

      for (const spec of ROWS) {
        if (!wantsMarket(spec.market)) {
          continue
        }
        if (spec.needs && (sharp[spec.needs] === null || sharp[spec.needs] === undefined)) {
          continue
        }
        const our = soft[spec.price]
        const pin = sharp[spec.price]
        if (!usable(our) || !usable(pin)) {
          continue
        }

        // the sharp row carries the yield of our price against itself; fall back to the arithmetic
        const published = sharp[spec.yield]
        const gap = typeof published === 'number' ? published : (our / pin - 1) * 100
        if (!inGap(gap)) {
          continue
        }

        out.push({
          key: `${event.home}|${event.away}|${event.koTime}|${spec.market}|${spec.price}`,
          game_key: `${event.home}|${event.away}|${event.koTime}`,
          home: event.home,
          away: event.away,
          koTime: event.koTime,
          fav: Boolean(event.fav),
          game: `${event.home} v ${event.away}`,
          sport: event.sport,
          league: event.league,
          market: spec.market,
          selection: spec.selection(event, sharp),
          our,
          pin,
          betfair: betfair && usable(betfair[spec.price]) ? betfair[spec.price] : null,
          gap
        })
      }
    }

    // soonest first: the top of the board is whatever is about to start
    out.sort((a, b) => a.koTime - b.koTime || a.game.localeCompare(b.game))

    /**
     * Six or seven selections belong to one fixture, and repeating the kick off and the teams down every
     * one of them turns the board into a wall. Only the first row of a game carries them; the rest are
     * blank under it and the group is closed off with a rule above the next one.
     */
    let previous = null
    for (const row of out) {
      const game = `${row.game}|${row.koTime}`
      row.firstOfGame = game !== previous
      previous = game
    }
    // and the panel belongs under the last of them, not between the first and the second
    for (let i = 0; i < out.length; i++) {
      out[i].lastOfGame = i === out.length - 1 || out[i + 1].firstOfGame
    }

    return out
  }

  const [ frozen, setFrozen ] = useState([])
  const ready = events !== null

  useEffect(() => {
    if (!ready) {
      return
    }
    setFrozen(buildRows())
    // feedToken is what makes this correct now that the server filters: when a picker moves, the events
    // in hand are still the previous page, and the rows have to be rebuilt again when the new one lands
    // eslint-disable-next-line
  }, [ready, feedToken, filters.sport, filters.leagues, filters.teams, filters.gapRange, filters.markets, filters.within, filters.favOnly])

  /**
   * The settled rows, wearing the latest prices. A row whose event has left the feed keeps the last
   * numbers it was given rather than emptying out — it is still the line somebody was looking at.
   */
  const rows = useMemo(() => {
    if (!frozen.length) {
      return frozen
    }
    const wanted = new Set(frozen.map((row) => `${row.home}|${row.away}|${row.koTime}`))
    const live = new Map()

    for (const event of events || []) {
      if (!wanted.has(`${event.home}|${event.away}|${event.koTime}`)) {
        continue
      }
      const soft = findSoftRow(event.bookieList)
      const sharp = findSharpRow(event.bookieList)
      const betfair = (event.bookieList || []).find((row) => BETFAIR.test(row.bookmaker || ''))
      if (!soft || !sharp) {
        continue
      }
      for (const spec of ROWS) {
        if (spec.needs && (sharp[spec.needs] === null || sharp[spec.needs] === undefined)) {
          continue
        }
        const our = soft[spec.price]
        const pin = sharp[spec.price]
        if (!usable(our) || !usable(pin)) {
          continue
        }
        const published = sharp[spec.yield]
        live.set(`${event.home}|${event.away}|${event.koTime}|${spec.market}|${spec.price}`, {
          our,
          pin,
          betfair: betfair && usable(betfair[spec.price]) ? betfair[spec.price] : null,
          gap: typeof published === 'number' ? published : (our / pin - 1) * 100,
          /**
           * The handicap and the total travel with the price. A row keyed on the market alone would
           * otherwise go on saying "Over 22.5" while the numbers beside it had already been rewritten
           * from the book's new line of 23 — the price of a bet other than the one it names.
           */
          selection: spec.selection({ home: event.home, away: event.away }, sharp),
          fav: Boolean(event.fav),
          started: Number(event.koTime) <= Date.now(),
          stale: false
        })
      }
    }

    return frozen.map((row) => {
      const now = live.get(row.key)
      // no live pair any more: the book pulled the price, or moved off this market altogether
      return now ? { ...row, ...now } : { ...row, stale: true }
    })
  }, [frozen, events])

  /**
   * Every price on this board is re-read every couple of seconds, and a number that changes silently
   * looks exactly like a number that is stuck. A moved price is tinted for three seconds — green when it
   * lengthened, red when it shortened — so the board is visibly alive without anyone having to watch one
   * cell. One timer per price, so a second move does not get cleared early by the first one's timer.
   */
  const [ moved, setMoved ] = useState(new Map())
  const previousPrices = useRef(new Map())
  const moveTimers = useRef({})

  useEffect(() => () => {
    Object.values(moveTimers.current).forEach(clearTimeout)
  }, [])

  /**
   * A new set of rows is a new baseline rather than a comparison against whatever was on screen before.
   * Without this, the prices that moved while the board sat empty — or while it showed another sport —
   * all arrive at once and light up as if the market had jumped in a single tick.
   */
  const baseline = useRef(null)
  useEffect(() => {
    if (baseline.current !== frozen) {
      baseline.current = frozen
      previousPrices.current = new Map()
      frozen.forEach((row) => {
        previousPrices.current.set(`${row.key}|our`, row.our)
        previousPrices.current.set(`${row.key}|pin`, row.pin)
      })
    }
  }, [frozen])

  useEffect(() => {
    if (!rows.length) {
      return
    }
    const current = new Map()
    rows.forEach((row) => {
      current.set(`${row.key}|our`, row.our)
      current.set(`${row.key}|pin`, row.pin)
    })

    const changes = []
    current.forEach((price, key) => {
      const before = previousPrices.current.get(key)
      if (before !== undefined && before !== price) {
        // decimal prices throughout, so the bigger number is the better one
        changes.push([key, price > before ? 'up' : 'down'])
      }
    })
    previousPrices.current = current

    if (!changes.length) {
      return
    }
    setMoved((prev) => {
      const next = new Map(prev)
      changes.forEach(([key, direction]) => next.set(key, direction))
      return next
    })
    changes.forEach(([key]) => {
      clearTimeout(moveTimers.current[key])
      moveTimers.current[key] = setTimeout(() => {
        delete moveTimers.current[key]
        setMoved((prev) => {
          const next = new Map(prev)
          next.delete(key)
          return next
        })
      }, MOVE_MS)
    })
  }, [rows])

  const tintOf = (key) => {
    const direction = moved.get(key)
    return direction ? { backgroundColor: MOVE_TINT[direction], transition: 'background-color 0.4s' } : {}
  }

  /**
   * The first two hundred games, whole. Cutting the list at a number of rows would leave a fixture with
   * three of its seven selections on screen and the rest missing.
   */
  const shown = useMemo(() => {
    const games = new Set()
    const out = []
    for (const row of rows) {
      if (!games.has(row.game_key)) {
        if (games.size === MAX_GAMES) {
          break
        }
        games.add(row.game_key)
      }
      out.push(row)
    }
    return out
  }, [rows])

  const gameCount = useMemo(() => new Set(rows.map((row) => row.game_key)).size, [rows])

  // the answer is a full page, so there are almost certainly more games behind it: the server sends the
  // soonest MAX_EVENTS and says nothing about what it left out
  const capped = (events || []).length >= MAX_EVENTS

  return (
    <Box>
      <Box>
        {error && (
          <Typography sx={{ fontFamily: FONT, fontSize: '13px', color: theme.palette.secondary.main }}>{error}</Typography>
        )}

        {events === null && !error && (
          <Box sx={{ display: 'flex', justifyContent: 'center', padding: '40px' }}><CircularProgress size={26} /></Box>
        )}

        {events !== null && (
          <Box>
            {/* above the table, not below it: a warning a thousand rows down is a warning nobody reads */}
            {(gameCount > MAX_GAMES || capped) && (
              <Typography sx={{ fontFamily: FONT, fontSize: '12px', color: '#9A9EA3', paddingBottom: '10px' }}>
                Showing the games that start soonest. Narrow the filters for the rest.
              </Typography>
            )}
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontFamily: FONT }}>
              <Box component="thead">
                <Box component="tr">
                  <Box component="th" sx={{ ...headSx, width: '28px' }} />
                  <Box component="th" sx={headSx}>Kick off ({clock.label})</Box>
                  <Box component="th" sx={headSx}>Game</Box>
                  <Box component="th" sx={headSx}>Competition</Box>
                  <Box component="th" sx={headSx}>Market</Box>
                  <Box component="th" sx={headSx}>Selection</Box>
                  <Box component="th" sx={headSx}>Our</Box>
                  <Box component="th" sx={headSx}>Pin</Box>
                  <Box component="th" sx={headSx}>BF</Box>
                  <Box component="th" sx={headSx}>Gap</Box>
                </Box>
              </Box>
              <Box component="tbody">
                {shown.map((row) => {
                  // the rule sits above the first row of each game, so a group is bounded on both sides
                  const groupSx = row.firstOfGame
                    ? { borderTop: '2px solid #DEDEE4', paddingTop: '11px' }
                    : {}
                  const isOpen = open.has(row.game_key)
                  return (
                  <Fragment key={row.key}>
                  <Box component="tr" sx={{ '&:hover': { backgroundColor: '#F8F8FA' } }}>
                    <Box component="td" sx={{ ...cellSx, ...groupSx, padding: '6px 0 6px 6px' }}>
                      {row.firstOfGame && (
                        <Box
                          component="button"
                          type="button"
                          aria-expanded={isOpen}
                          aria-label={isOpen ? 'Hide every line of this match' : 'Show every line of this match'}
                          onClick={() => toggle(row.game_key)}
                          sx={{
                            appearance: 'none',
                            border: 'none',
                            background: 'none',
                            cursor: 'pointer',
                            padding: '2px 4px',
                            color: '#9A9EA3',
                            fontSize: '11px',
                            lineHeight: 1,
                            transform: isOpen ? 'rotate(90deg)' : 'none',
                            transition: 'transform 0.15s',
                            '&:hover': { color: theme.palette.primary.light }
                          }}
                        >
                          ▶
                        </Box>
                      )}
                    </Box>
                    <Box
                      component="td"
                      sx={{ ...cellSx, ...groupSx, color: row.started ? theme.palette.secondary.main : '#6E7378', fontVariantNumeric: 'tabular-nums' }}
                      title={row.started ? 'This fixture has started: the prices beside it are in play' : undefined}
                    >
                      {row.firstOfGame ? (row.started ? 'In play' : clock.kickOff(row.koTime)) : ''}
                    </Box>
                    <Box component="td" sx={{ ...cellSx, ...groupSx, fontWeight: 600 }}>
                      {row.firstOfGame ? (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Favorite compact event={row} />
                          <Box
                            component="span"
                            onClick={() => navigate(`${pathForEvent(row)}?book=pinnacle`)}
                            sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline', color: theme.palette.primary.light } }}
                          >
                            {row.game}
                          </Box>
                        </Box>
                      ) : ''}
                    </Box>
                    <Box component="td" sx={{ ...cellSx, ...groupSx, color: '#9A9EA3' }}>
                      {row.firstOfGame ? `${row.sport} · ${row.league}` : ''}
                    </Box>
                    <Box component="td" sx={{ ...cellSx, ...groupSx, color: '#6E7378' }}>{row.market}</Box>
                    <Box component="td" sx={{ ...cellSx, ...groupSx }}>{row.selection}</Box>
                    <Box
                      component="td"
                      title={row.stale ? 'No longer quoted: this is the last price the book showed' : undefined}
                      sx={{ ...cellSx, ...groupSx, ...tintOf(`${row.key}|our`), fontWeight: 700, color: row.stale ? '#B4B8BC' : 'inherit', fontVariantNumeric: 'tabular-nums' }}
                    >
                      {formatDecimalPrice(row.our, usPrices)}{row.stale ? ' *' : ''}
                    </Box>
                    <Box
                      component="td"
                      sx={{ ...cellSx, ...groupSx, ...tintOf(`${row.key}|pin`), color: row.stale ? '#B4B8BC' : 'inherit', fontVariantNumeric: 'tabular-nums' }}
                    >
                      {formatDecimalPrice(row.pin, usPrices)}
                    </Box>
                    <Box component="td" sx={{ ...cellSx, ...groupSx, color: '#9A9EA3', fontVariantNumeric: 'tabular-nums' }}>
                      {row.betfair === null ? '—' : formatDecimalPrice(row.betfair, usPrices)}
                    </Box>
                    <Box
                      component="td"
                      sx={{
                        ...cellSx,
                        ...groupSx,
                        fontWeight: 700,
                        fontVariantNumeric: 'tabular-nums',
                        color: row.gap > 0 ? theme.palette.secondary.main : '#9A9EA3'
                      }}
                    >
                      {row.gap > 0 ? '+' : ''}{row.gap.toFixed(2)}%
                    </Box>
                  </Box>
                  {isOpen && row.lastOfGame && (
                    <Box component="tr">
                      <Box component="td" colSpan={10} sx={{ padding: 0, backgroundColor: '#FBFBFC' }}>
                        <EventMarkets event={row} book="pinnacle" pollingInterval={pollingInterval} />
                      </Box>
                    </Box>
                  )}
                  </Fragment>
                  )
                })}
              </Box>
            </Box>

            {shown.length === 0 && (
              <Typography sx={{ fontFamily: FONT, fontSize: '14px', color: '#9A9EA3', padding: '24px 0' }}>
                Nothing matches these filters.
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default SelectionsTable
